/// <reference path="../../../../../typings/index.d.ts" />

module Meevo.Plugins.MeevoPaySummary {
    /**
     * Registers the meevo-meevopay-summary custom element. No access check here: the
     * Summary API decides who may see a location's summary, and the panel renders
     * whatever that API allows.
     */
    angular.module("meevo.plugins.meevoPaySummary", [])
        .run(['webComponentSetup', '$rootScope', 'meevo.AppSettings',
            function (
                webComponentSetup: any,
                $rootScope: angular.IRootScopeService,
                appSettings: Meevo.Components.Common.IAppSettings
            ) {
                // Angular Elements outputs do not bubble, so the listeners sit on the element.
                var transferListeners = new WeakMap<HTMLElement, EventListener>();
                var detailLevelListeners = new WeakMap<HTMLElement, EventListener>();
                var transactionListeners = new WeakMap<HTMLElement, EventListener>();
                // A Details widening that waits for the dock to land; cancelled by a later level or by unmount.
                var pendingWidens = new WeakMap<HTMLElement, () => void>();

                // A chargeback's way into Meevo: the menu closes, then the Transaction Editor opens as everywhere else
                // does it (the client history's "open transaction", the appointment book's "View Transaction").
                // GOTO_STATE checks the security key and warns instead of opening when the user may not.
                function gotoFromMenu(route: any): void {
                    $rootScope.$applyAsync(function () {
                        $rootScope.$broadcast(Meevo.Components.Common.Messages.CLOSE_MENU);
                        $rootScope.$broadcast(Meevo.Components.Common.Messages.GOTO_STATE, route);
                    });
                }

                // { transactionId } opens that transaction; { externalTransactionId } searches for Adyen's reference
                // (a payment Meevo could not place). Either way at the session's location, as the Summary's are.
                function openTransaction(event: Event): void {
                    var detail = (<CustomEvent>event).detail || {};
                    var criteria: any = detail.transactionId
                        ? { transactionId: Number(detail.transactionId) }
                        : detail.externalTransactionId ? { externalTransactionId: String(detail.externalTransactionId) } : null;
                    if (!criteria) {
                        return;
                    }
                    gotoFromMenu({
                        stateName: appSettings.ROUTE_NAMES.transactionEditor,
                        stateParams: angular.extend(criteria, {
                            includeActive: true,
                            includeCompleted: true,
                            includeOnHold: false,
                            includeRefunded: true,
                            includeVoided: true,
                        }),
                        securityKey: Meevo.Components.Common.SecurityKeys.REGISTER_TRANSACTIONEDITOR
                            + Meevo.Components.Common.SecurityParts.KEY_PART_READ,
                    });
                }
                // The panel owns its detail level (and remembers it); the dock only widens for Details. The
                // phone panel is full width already and has no dock, so there it changes nothing.
                var DETAILS_DOCK_CLASS = 'meevopay-summary-dock--details';

                // One choreography, whichever way Details comes: the dock trails the menu out by about BEAT_MS, and
                // Details widens the dock BEAT_MS after the dock has landed, on the same curve and duration (the
                // styles). A Details restored at mount waits for the slide; a toggle on a landed dock has nothing to
                // wait for and widens at once. While the dock is still entering, its enter transition replaces the
                // width transition, so widening then would snap rather than ease.
                var BEAT_MS = 100;
                // The slide's curve (cubic-bezier(0.25, 0.8, 0.25, 1)) has under a pixel left at 90% of its time: that
                // is when the dock looks landed, so the beat counts from there rather than from the formal end.
                var LOOKS_LANDED_AT = 0.9;
                // Past the enter's 100ms delay and 400ms slide: the widening never waits on an enter that never ends.
                var ENTER_FALLBACK_MS = 800;

                function isEntering(dock: Element): boolean {
                    return dock.classList.contains('ng-enter') || dock.classList.contains('ng-enter-prepare');
                }

                function prefersReducedMotion(): boolean {
                    return typeof window.matchMedia === 'function'
                        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                }

                /** The dock's running slide, once the browser has started it; null before then or without the API. */
                function runningSlide(dock: HTMLElement): any {
                    var animations: any[] = typeof (<any>dock).getAnimations === 'function' ? (<any>dock).getAnimations() : [];
                    for (var i = 0; i < animations.length; i++) {
                        if (animations[i].transitionProperty === 'transform' && animations[i].playState === 'running') {
                            return animations[i];
                        }
                    }
                    return null;
                }

                /**
                 * Calls widen a beat after the dock looks landed; returns the cancel. Read from the slide transition's
                 * own timing (it exists from the enter's first frame, delay included): transitionend does not reliably
                 * reach us, and ngAnimate drops the enter classes on its own, later timer. Until the slide exists it
                 * watches each frame; without the API, it waits for the classes to go.
                 */
                function afterDockLands(dock: HTMLElement, widen: () => void): () => void {
                    var frame: number = null;
                    var beat: number = null;
                    var started = Date.now();
                    function watch(): void {
                        var slide = runningSlide(dock);
                        if (slide) {
                            var timing = slide.effect.getComputedTiming();
                            var looksLanded = timing.endTime - (1 - LOOKS_LANDED_AT) * timing.duration;
                            beat = window.setTimeout(widen, Math.max(0, looksLanded - slide.currentTime) + BEAT_MS);
                        } else if (isEntering(dock) && Date.now() - started < ENTER_FALLBACK_MS) {
                            frame = window.requestAnimationFrame(watch);
                        } else {
                            beat = window.setTimeout(widen, BEAT_MS);
                        }
                    }
                    frame = window.requestAnimationFrame(watch);
                    return function cancel(): void {
                        window.cancelAnimationFrame(frame);
                        window.clearTimeout(beat);
                    };
                }

                function cancelPendingWiden(element: HTMLElement): void {
                    var cancel = pendingWidens.get(element);
                    if (cancel) {
                        cancel();
                        pendingWidens.delete(element);
                    }
                }

                webComponentSetup.register({
                    id: 'meevopay-summary',
                    // Read at every mount, not at module run: the business settings load
                    // after authentication. Same predicate the side menu uses to pick
                    // the enhanced (light) or legacy (dark) menu.
                    preMountProps: function () {
                        var businessInfo: any = appSettings.APP_BUSINFOSETTINGS;
                        var enhanced = !!(businessInfo
                            && businessInfo.generalSettings
                            && businessInfo.generalSettings.enableEnhancedGlobalNavigation);
                        return { theme: enhanced ? 'light' : 'dark' };
                    },
                    // Transfer Now and both View Details open MeevoXchange's MeevoPay page (MeevoPay Now, the
                    // account's details, its disputes) in the Shell's MeevoXchange tab: the same route the
                    // "MeevoPay Account Management" menu item takes. The menu closes, and that page builds its
                    // short-lived sign-in link when it opens.
                    onMount: function (element: HTMLElement) {
                        var openMeevoXchange: EventListener = function () {
                            $rootScope.$applyAsync(function () {
                                $rootScope.$broadcast(
                                    Meevo.Components.Common.Messages.MENU_ITEM_SELECTED,
                                    appSettings.ROUTE_NAMES.meevoStore,
                                    { linkType: Meevo.LinkType.MeevoPay }
                                );
                            });
                        };
                        transferListeners.set(element, openMeevoXchange);
                        element.addEventListener('transferRequested', openMeevoXchange);
                        element.addEventListener('detailsRequested', openMeevoXchange);

                        transactionListeners.set(element, openTransaction);
                        element.addEventListener('transactionRequested', openTransaction);

                        var onDetailLevelChanged: EventListener = function (event: Event) {
                            var dock = <HTMLElement>element.closest('.meevopay-summary-dock');
                            if (!dock) {
                                return;
                            }
                            cancelPendingWiden(element);
                            var details = (<CustomEvent>event).detail === 'details';
                            if (details && isEntering(dock) && !prefersReducedMotion()) {
                                pendingWidens.set(element, afterDockLands(dock, function () {
                                    pendingWidens.delete(element);
                                    dock.classList.add(DETAILS_DOCK_CLASS);
                                }));
                                return;
                            }
                            dock.classList.toggle(DETAILS_DOCK_CLASS, details);
                        };
                        detailLevelListeners.set(element, onDetailLevelChanged);
                        element.addEventListener('detailLevelChanged', onDetailLevelChanged);
                    },
                    onUnmount: function (element: HTMLElement) {
                        cancelPendingWiden(element);
                        var openMeevoXchange = transferListeners.get(element);
                        if (openMeevoXchange) {
                            element.removeEventListener('transferRequested', openMeevoXchange);
                            element.removeEventListener('detailsRequested', openMeevoXchange);
                            transferListeners.delete(element);
                        }
                        var onTransaction = transactionListeners.get(element);
                        if (onTransaction) {
                            element.removeEventListener('transactionRequested', onTransaction);
                            transactionListeners.delete(element);
                        }
                        var onDetailLevelChanged = detailLevelListeners.get(element);
                        if (onDetailLevelChanged) {
                            element.removeEventListener('detailLevelChanged', onDetailLevelChanged);
                            detailLevelListeners.delete(element);
                        }
                    },
                });
            }]);
}
